import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';

import { ComponentTypes } from './models/component-types.enum';
import { RxAutocompleteComponent, RxAutocompleteModule } from '@nrwl/rx-autocomplete';
import { RxCheckboxComponent, RxCheckboxModule } from '@nrwl/rx-checkbox';
import { RxComposite } from '../model/rx-composite';
import { RxInputComponent, RxInputModule } from '@nrwl/rx-input';
import { RxTextareaComponent, RxTextareaModule } from '@nrwl/rx-textarea';
import {
  RxInputInternationalPhoneComponent,
  RxInputInternationalPhoneModule,
} from '@nrwl/rx-input-intl-phone';
import { RxListModule, RxListRadioItemComponent } from '@nrwl/rx-list';
import { RxRadioGroupComponent, RxRadioGroupModule } from '@nrwl/rx-radio-group';
import { RxSelectComponent, RxSelectModule } from '@nrwl/rx-select';
import { RxToggleComponent, RxToggleModule } from '@nrwl/rx-toggle';
import { RxUploadThumbnailComponent } from 'libs/rx-ui/src/lib/rx-upload-thumbnail/rx-upload-thumbnail/rx-upload-thumbnail.component';
import { RxRowComponent } from 'libs/rx-ui/src/lib/rx-row/rx-row.component';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { RxButtonModule } from '@nrwl/rx-button';
import { RxIconModule } from '@nrwl/rx-icon';
import { RxFormControlErrorsModule } from '@nrwl/rx-form-errors';
import { RxTipModule } from '@nrwl/rx-tip';
import { RxUploadThumbnailModule } from '@nrwl/rx-ui';

@Component({
  selector: 'rx-reactive-form-composite',
  templateUrl: './rx-reactive-form-composite.component.html',
  styleUrls: ['./rx-reactive-form-composite.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RxTipModule,
    RxInputModule,
    RxIconModule,
    RxButtonModule,
    RxFormControlErrorsModule,
    RxSelectModule,
    RxAutocompleteModule,
    RxListModule,
    RxToggleModule,
    RxTextareaModule,
    RxInputInternationalPhoneModule,
    RxCheckboxModule,
    RxRadioGroupModule,
    RxUploadThumbnailModule,
  ],
})
export class RxReactiveFormCompositeComponent extends RxComposite implements OnInit {
  @ViewChild('appointmentTemplate', { static: true })
  appointmentTemplate: TemplateRef<any>;
  public formGroup: FormGroup;
  public componentTypes = ComponentTypes;
  public originalValues: string[] = [];
  public isFormDirty = false;

  @Input() public searchable: boolean;

  @Input()
  override set payload(value) {
    if (value?.tip) {
      this.sanitizeTip(value.tip);
    }
    if (value?.subTitle) {
      this.sanitizeSubtitle(value.subTitle);
    }
    if (this._payload_2 !== value) {
      this._payload_2 = value;

      if (value.fields) {
        this.originalValues = value.fields.map((f) =>
          typeof f?.value === 'string' ? f.value?.trim() || '' : f.value,
        ); // ✅ initialize here
        value.fields.forEach((item, ind) => {
          const field = this.createField(item);
          this._payload_2.fields[ind].componentType = this._formComponentType(item);
          this.fields.push(field);
        });
      }
    }
  }

  private _payload_2: any;

  sanitizedTip: SafeHtml;
  sanitizedSubtitle: SafeHtml;

  constructor(
    private _formBuilder: FormBuilder,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
  ) {
    super();
    this.createForm();
  }

  override get payload() {
    return this._payload_2;
  }

  get fields(): FormArray {
    return this.formGroup.get('fields') as FormArray;
  }

  public ngOnInit() {
    this.formGroup.valueChanges.subscribe(() => {
      this.cdr.markForCheck(); // or this.cdr.detectChanges()
    });
    this.formGroup.valueChanges.subscribe(() => {
      this.isFormDirty = this.checkIfDirty();
      this.cdr.markForCheck();
    });
  }
  private checkIfDirty(): boolean {
    return this.fields.controls.some((ctrl, i) => {
      const current =
        typeof ctrl.get('value')?.value === 'string'
          ? ctrl.get('value')?.value?.trim()
          : ctrl.get('value')?.value;
      const original =
        typeof this.originalValues[i] === 'string'
          ? this.originalValues[i]?.trim()
          : this.originalValues[i];
      return current !== original;
    });
  }

  sanitizeTip(value: any) {
    this.sanitizedTip = this.sanitizer.bypassSecurityTrustHtml(value);
  }

  sanitizeSubtitle(value: any) {
    this.sanitizedSubtitle = this.sanitizer.bypassSecurityTrustHtml(value);
  }

  public createForm() {
    this.formGroup = this._formBuilder.group({
      fields: new FormArray([]),
    });
    this.formGroup.valueChanges.subscribe((form) => this.isValid());
    this.formGroup.valueChanges.subscribe(() => {
      this.cdr.markForCheck(); // <- THIS will trigger Angular to reevaluate bindings
    });
  }

  public createField(field) {
    const item = this._formBuilder.group({
      value: [field.value, field.validators, field.asyncValidators],
    });
    if (field.selectType === 'appointment') {
      field.optionTemplate = this.appointmentTemplate;
    }
    return item;
  }

  public override isValid() {
    if (!this.formGroup.pending && this.formGroup.valid) {
      // sync form and payload to reflect changes on submit
      this.payload.fields.forEach((item, index) => {
        if (this.fields.value[index]) {
          item.value = this.fields.value[index].value;
        }
      });
      return true;
    }
    return false;
  }

  private _validate(formGroup: FormGroup) {
    // @TODO
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
        control.markAsDirty({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this._validate(control);
      } else if (control instanceof FormArray) {
        (control as FormArray).controls.forEach((ctrl) => {
          Object.keys((ctrl as FormGroup).controls).forEach((nControl) => {
            const nestedControl: any = ctrl.get(nControl);
            nestedControl.markAsDirty();
            nestedControl.markAsTouched();
            nestedControl.updateValueAndValidity();
          });
        });
      }
    });
  }

  public override validate() {
    this._validate(this.formGroup);
  }

  private _formComponentType(item): any {
    if (!item.component) {
      return this.componentTypes.Input;
    }

    switch (item.component) {
      case RxInputComponent: {
        return this.componentTypes.Input;
      }
      case RxTextareaComponent: {
        return this.componentTypes.Textarea;
      }
      case RxToggleComponent: {
        return this.componentTypes.Toggle;
      }
      case RxListRadioItemComponent: {
        return this.componentTypes.Radio;
      }
      case RxSelectComponent: {
        return this.componentTypes.Select;
      }
      case RxAutocompleteComponent: {
        return this.componentTypes.Autocomplete;
      }
      case RxInputInternationalPhoneComponent: {
        return this.componentTypes.Phone;
      }
      case RxRadioGroupComponent: {
        return this.componentTypes.RadioGroup;
      }
      case RxUploadThumbnailComponent: {
        return this.componentTypes.UploadThumbnail;
      }
      case RxCheckboxComponent: {
        return this.componentTypes.Checkbox;
      }
      case RxRowComponent: {
        return this.componentTypes.HorizontalLine;
      }
    }
    return;
  }
}
